import { useCallback, useEffect, useRef, useState } from "react";
import {
  Button,
  Menu,
  MenuItem,
  Stack,
  Typography,
  Box,
  Tooltip,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CloudOutlinedIcon from "@mui/icons-material/CloudOutlined";
import { handleErrorMessage, openErrorNotification } from "helpers/methods";
import {
  fetchAuthUrls,
  fetchCloudFiles,
  fetchIntegratedServices,
  searchCloudFiles,
  uploadCloudFile,
} from "./CloudStorageService";
import { cloudProviders, getProviderByKey } from "./providers";
import { CloudFile, CloudProvider, CloudUploadResponse } from "./types";
import CloudFilesDialog from "./CloudFilesDialog";
import styles from "./CloudStoragePicker.styles";
import {
  AUTH_CODE_PROVIDER_KEY,
  AUTO_OPEN_PROVIDER_KEY,
} from "./CloudStorageOAuthCallback";

interface CloudStoragePickerProps {
  envelopeId: string;
  ensureEnvelopeId: () => Promise<string>;
  onFileUploaded: (
    provider: string,
    fileName: string,
    response: CloudUploadResponse,
    envelopeId: string,
  ) => void;
  disabled?: boolean;
  disabledTooltip?: string;
  setLoading?: (loading: boolean) => void;
  buttonLabel?: string;
}

const CloudStoragePicker = ({
  envelopeId,
  ensureEnvelopeId,
  onFileUploaded,
  disabled,
  disabledTooltip,
  setLoading,
  buttonLabel = "From cloud",
}: CloudStoragePickerProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [integratedProviders, setIntegratedProviders] = useState<string[]>([]);
  const [activeProvider, setActiveProvider] = useState<CloudProvider | null>(
    null,
  );
  const [dialogOpen, setDialogOpen] = useState(false);
  const [files, setFiles] = useState<CloudFile[]>([]);
  const [folderHistory, setFolderHistory] = useState<string[]>([]);
  const autoOpenHandledRef = useRef(false);

  const withLoader = useCallback(
    async <T,>(fn: () => Promise<T>): Promise<T> => {
      try {
        setLoading?.(true);
        return await fn();
      } finally {
        setLoading?.(false);
      }
    },
    [setLoading],
  );

  const loadAuthUrls = useCallback(async (): Promise<
    Record<string, string>
  > => {
    try {
      return (await fetchAuthUrls()) || {};
    } catch (error: any) {
      handleErrorMessage(error);
      return {};
    }
  }, []);

  const loadIntegratedProviders = useCallback(async () => {
    try {
      const res = await fetchIntegratedServices();
      setIntegratedProviders(Array.isArray(res) ? res : []);
    } catch (error: any) {
      handleErrorMessage(error);
    }
  }, []);

  const openFileDialog = useCallback(
    async (provider: CloudProvider, path: string = "") => {
      try {
        await withLoader(async () => {
          const res = await fetchCloudFiles(provider.key, path);
          setFiles(Array.isArray(res) ? res : []);
        });
        setDialogOpen(true);
      } catch (error: any) {
        handleErrorMessage(error);
        if (error?.message?.toLowerCase().includes("expired")) {
          await loadIntegratedProviders();
          await loadAuthUrls();
        }
      }
    },
    [withLoader, loadIntegratedProviders, loadAuthUrls],
  );

  useEffect(() => {
    loadIntegratedProviders();
  }, [loadIntegratedProviders]);

  useEffect(() => {
    if (autoOpenHandledRef.current) return;
    const providerKey = sessionStorage.getItem(AUTO_OPEN_PROVIDER_KEY);
    if (!providerKey) return;
    const provider = getProviderByKey(providerKey);
    if (!provider) {
      sessionStorage.removeItem(AUTO_OPEN_PROVIDER_KEY);
      return;
    }
    autoOpenHandledRef.current = true;
    sessionStorage.removeItem(AUTO_OPEN_PROVIDER_KEY);
    (async () => {
      await loadIntegratedProviders();
      setActiveProvider(provider);
      setFolderHistory([]);
      await openFileDialog(provider);
    })();
  }, [loadIntegratedProviders, openFileDialog]);

  const handleTriggerClick = (event: React.MouseEvent<HTMLElement>) => {
    if (disabled) return;
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => setAnchorEl(null);

  const handleProviderSelect = async (provider: CloudProvider) => {
    handleMenuClose();
    setActiveProvider(provider);
    setFolderHistory([]);

    if (integratedProviders.includes(provider.key)) {
      await openFileDialog(provider);
      return;
    }

    // Only fetch auth URLs on demand — backend session gets touched here, so
    // avoid extra concurrent calls from mount-time or repeated clicks.
    const refreshed = await withLoader(() => loadAuthUrls());
    const url = refreshed[provider.key];
    if (!url) {
      openErrorNotification(
        `Authentication URL for ${provider.displayName} is not available yet. Please try again.`,
      );
      return;
    }
    sessionStorage.setItem(AUTH_CODE_PROVIDER_KEY, provider.key);
    window.location.replace(url);
  };

  const handleSearchChange = async (term: string) => {
    if (!activeProvider) return;
    try {
      await withLoader(async () => {
        if (!term.trim()) {
          const res = await fetchCloudFiles(
            activeProvider.key,
            folderHistory[folderHistory.length - 1] ?? "",
          );
          setFiles(Array.isArray(res) ? res : []);
          return;
        }
        const res = await searchCloudFiles(activeProvider.key, term.trim());
        setFiles(Array.isArray(res) ? res : []);
      });
    } catch (error: any) {
      handleErrorMessage(error);
    }
  };

  const handleOpenFolder = async (file: CloudFile) => {
    if (!activeProvider) return;
    const nextPath = file.path ?? file.id ?? "";
    try {
      await withLoader(async () => {
        const res = await fetchCloudFiles(activeProvider.key, nextPath);
        setFiles(Array.isArray(res) ? res : []);
      });
      setFolderHistory((prev) => [...prev, nextPath]);
    } catch (error: any) {
      handleErrorMessage(error);
    }
  };

  const handleGoBack = async () => {
    if (!activeProvider) return;
    const next = folderHistory.slice(0, -1);
    const parent = next[next.length - 1] ?? "";
    try {
      await withLoader(async () => {
        const res = await fetchCloudFiles(activeProvider.key, parent);
        setFiles(Array.isArray(res) ? res : []);
      });
      setFolderHistory(next);
    } catch (error: any) {
      handleErrorMessage(error);
    }
  };

  const handleUpload = async (file: CloudFile) => {
    if (!activeProvider) return;
    try {
      const targetEnvelopeId = envelopeId || (await ensureEnvelopeId());
      const response = await withLoader(() =>
        uploadCloudFile(activeProvider.key, targetEnvelopeId, {
          id: file.id,
          name: file.name,
        }),
      );
      const uploadedName = response?.message || file.name;
      onFileUploaded(
        activeProvider.key,
        uploadedName,
        response,
        targetEnvelopeId,
      );
      setDialogOpen(false);
      setFiles([]);
      setFolderHistory([]);
    } catch (error: any) {
      handleErrorMessage(error);
    }
  };

  const trigger = (
    <Button
      variant="outlined"
      startIcon={<CloudOutlinedIcon />}
      endIcon={<ArrowDropDownIcon />}
      onClick={handleTriggerClick}
      disabled={disabled}
      aria-haspopup="true"
      aria-expanded={Boolean(anchorEl)}
      aria-controls={anchorEl ? "cloud-provider-menu" : undefined}
      sx={styles.triggerBtn}
    >
      {buttonLabel}
    </Button>
  );

  return (
    <>
      {disabled && disabledTooltip ? (
        <Tooltip title={disabledTooltip} arrow>
          <Box component="span" sx={{ display: "inline-flex" }}>
            {trigger}
          </Box>
        </Tooltip>
      ) : (
        trigger
      )}

      <Menu
        id="cloud-provider-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        keepMounted
        MenuListProps={{ "aria-label": "Cloud storage providers" }}
      >
        {cloudProviders.map((provider) => (
          <MenuItem
            key={provider.key}
            onClick={() => handleProviderSelect(provider)}
            sx={styles.menuItem}
          >
            <Box
              component="img"
              src={provider.icon}
              alt=""
              sx={styles.menuItemIcon}
            />
            <Typography sx={styles.menuItemLabel}>
              {provider.displayName}
            </Typography>
            {integratedProviders.includes(provider.key) && (
              <CheckCircleRoundedIcon
                fontSize="small"
                titleAccess="Connected"
                sx={{ color: "success.main" }}
              />
            )}
          </MenuItem>
        ))}
      </Menu>

      {activeProvider && (
        <CloudFilesDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          provider={activeProvider}
          files={files}
          onSearchChange={handleSearchChange}
          onOpenFolder={handleOpenFolder}
          onGoBack={handleGoBack}
          showBackButton={
            activeProvider.supportsFolders && folderHistory.length > 0
          }
          onUpload={handleUpload}
        />
      )}
    </>
  );
};

export default CloudStoragePicker;
